<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  Manual verification
                </div>
                <h2>Verify Transaction</h2>
                <p>Confirm a payment straight from the bank receipt — no screenshot needed.</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <!-- Result view -->
            <div v-if="result" class="modal-form">
              <div class="modal-body">
                <section class="result-section" :class="result.alreadyVerified ? 'is-info' : 'is-success'">
                  <div class="result-icon">
                    <i class="pi pi-check"></i>
                  </div>
                  <h3>{{ result.alreadyVerified ? "Already verified" : "Transaction verified" }}</h3>
                  <p v-if="result.alreadyVerified">This transaction was verified earlier — here are its details.</p>
                  <p v-else>The payment was confirmed against the bank receipt.</p>

                  <dl class="result-grid">
                    <div class="result-row">
                      <dt>Amount</dt>
                      <dd class="result-amount">{{ amount(result) }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Bank</dt>
                      <dd>{{ bankLabel(result.bank) }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Reference</dt>
                      <dd class="mono">{{ result.transactionReference || result.transactionId || "—" }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Payer</dt>
                      <dd>{{ result.accountName || "—" }}</dd>
                    </div>
                    <div v-if="result.accountNumber" class="result-row">
                      <dt>Payer account</dt>
                      <dd class="mono">{{ result.accountNumber }}</dd>
                    </div>
                    <div v-if="result.receiverName" class="result-row">
                      <dt>Receiver</dt>
                      <dd>{{ result.receiverName }}</dd>
                    </div>
                    <div v-if="result.receiverAccountNumber" class="result-row">
                      <dt>Receiver account</dt>
                      <dd class="mono">{{ result.receiverAccountNumber }}</dd>
                    </div>
                    <div class="result-row">
                      <dt>Date</dt>
                      <dd>{{ $filters.getDateAndTime(result.date) }}</dd>
                    </div>
                  </dl>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="verifyAnother">Verify another</button>
                <button type="button" class="primary-button" v-on:click="done">Done</button>
              </footer>
            </div>

            <!-- Failure view -->
            <div v-else-if="failure" class="modal-form">
              <div class="modal-body">
                <section class="result-section is-error">
                  <div class="result-icon">
                    <i class="pi pi-times"></i>
                  </div>
                  <h3>Verification failed</h3>
                  <p>{{ failure }}</p>
                </section>
              </div>
              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Close</button>
                <button type="button" class="primary-button" v-on:click="tryAgain">Try again</button>
              </footer>
            </div>

            <!-- Form view -->
            <form v-else class="modal-form" v-on:submit.prevent="submit">
              <div class="modal-body">
                <section class="form-section">
                  <h3>Transaction Details</h3>

                  <!-- Choose how to verify: by reference id, or by pasting the receipt link. -->
                  <div class="mode-toggle" role="tablist" aria-label="How to verify">
                    <button
                      type="button"
                      class="mode-btn"
                      :class="{ active: inputMode === 'id' }"
                      role="tab"
                      :aria-selected="inputMode === 'id'"
                      v-on:click="setInputMode('id')"
                    >
                      <i class="pi pi-id-card"></i>
                      <span>Reference ID</span>
                    </button>
                    <button
                      type="button"
                      class="mode-btn"
                      :class="{ active: inputMode === 'url' }"
                      role="tab"
                      :aria-selected="inputMode === 'url'"
                      v-on:click="setInputMode('url')"
                    >
                      <i class="pi pi-link"></i>
                      <span>Receipt link</span>
                    </button>
                  </div>

                  <div class="form-grid">
                    <!-- Reference ID flow: pick the bank + account, then enter the reference. -->
                    <template v-if="inputMode === 'id'">
                      <label class="field full">
                        <span>Bank</span>
                        <select v-model="form.bank">
                          <option :value="null" disabled>Select bank</option>
                          <option v-for="bank in banks" :key="bank.value" :value="bank.value">{{ bank.label }}</option>
                        </select>
                        <span v-if="errors.bank" class="form-text text-danger">{{ errors.bank }}</span>
                      </label>

                      <label class="field full">
                        <span>
                          Bank Account
                          <small>{{ accountRequired ? "(required)" : "(optional)" }}</small>
                        </span>
                        <select v-model="form.recipientAccountId" :disabled="!form.bank">
                          <option :value="null">{{ form.bank ? "Select a bank account" : "Select a bank first" }}</option>
                          <option v-for="account in filteredAccounts" :key="account.id" :value="account.id">
                            {{ accountLabel(account) }}
                          </option>
                        </select>
                        <span v-if="errors.recipientAccountId" class="form-text text-danger">{{ errors.recipientAccountId }}</span>
                        <small v-if="form.bank && !bankAccountsLoading && filteredAccounts.length === 0" class="hint">
                          No {{ bankLabel(form.bank) }} accounts yet.
                          <a class="link" v-on:click="goToBankAccounts">Add one in Bank Accounts.</a>
                        </small>
                      </label>

                      <label class="field full">
                        <span>Reference ID</span>
                        <input
                          v-model.trim="form.transactionId"
                          type="text"
                          inputmode="text"
                          placeholder="e.g. FT25ABC12345"
                          autocomplete="off"
                          v-on:input="onRefInput"
                        />
                        <span v-if="errors.transactionId" class="form-text text-danger">{{ errors.transactionId }}</span>
                        <small class="hint">The transaction / receipt reference from the payment, letters and numbers only.</small>
                      </label>
                    </template>

                    <!-- Receipt link flow: just paste the bank's own receipt URL. The bank is
                         detected from the link and the account suffix is read straight from it,
                         so there is nothing else to choose. -->
                    <label v-else class="field full">
                      <span>Receipt link</span>
                      <input
                        v-model.trim="form.receiptUrl"
                        type="url"
                        inputmode="url"
                        placeholder="Paste the bank's receipt link"
                        autocomplete="off"
                        spellcheck="false"
                        v-on:input="onUrlInput"
                      />
                      <span v-if="errors.receiptUrl" class="form-text text-danger">{{ errors.receiptUrl }}</span>
                      <small v-if="detectedBankLabel" class="hint hint-detected">
                        <i class="pi pi-check-circle"></i>
                        Detected {{ detectedBankLabel }} — we'll verify this receipt.
                      </small>
                      <small v-else class="hint">
                        Paste the receipt link from the payment SMS or app — we'll detect the bank automatically.
                      </small>
                    </label>
                  </div>
                </section>
              </div>

              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Cancel</button>
                <button type="submit" class="primary-button" :disabled="submitting">
                  <span v-if="submitting" class="btn-spinner" aria-hidden="true"></span>
                  <span>{{ submitting ? "Verifying…" : "Verify" }}</span>
                </button>
              </footer>
            </form>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { GET_BANK_ACCOUNTS, VERIFY_TRANSACTION } from "@/store/action_types";
import { BANKS, BANK_LABELS } from "@/utils/enums";
import { BANK_ACCOUNTS_ROUTE } from "@/router/routes";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

export default {
  emits: ["close-verify-modal", "verified"],
  data() {
    return {
      showModal: true,
      // The /verify endpoint supports CBE, telebirr and Bank of Abyssinia.
      banks: [
        { label: BANK_LABELS[BANKS.cbe], value: BANKS.cbe },
        { label: BANK_LABELS[BANKS.telebirr], value: BANKS.telebirr },
        { label: BANK_LABELS[BANKS.boa], value: BANKS.boa },
      ],
      // "id" → enter the transaction reference; "url" → paste the bank's receipt link.
      inputMode: "id",
      form: {
        bank: null,
        recipientAccountId: null,
        transactionId: "",
        receiptUrl: "",
      },
      errors: {
        bank: "",
        recipientAccountId: "",
        transactionId: "",
        receiptUrl: "",
      },
      submitting: false,
      result: null,
      // Holds the reason a verification failed, shown in-modal (not just as a toast).
      failure: null,
    };
  },
  computed: {
    bankAccounts() {
      return this.$store.getters.bankAccounts.data || [];
    },
    bankAccountsLoading() {
      return this.$store.getters.bankAccounts.isLoading;
    },
    // Only accounts matching the chosen bank can be used (the backend rejects a mismatch).
    filteredAccounts() {
      return this.bankAccounts.filter(
        (a) => a.bank === this.form.bank && a.isActive && !a.isDeleted,
      );
    },
    // CBE and BOA resolve the receipt from the recipient account number, so an account is
    // required — but only in the reference-id flow. A pasted link already carries that suffix.
    accountRequired() {
      return (
        this.inputMode === "id" &&
        (this.form.bank === BANKS.cbe || this.form.bank === BANKS.boa)
      );
    },
    // In receipt-link mode the bank is identified from the pasted url (no selector), so show
    // which one was recognised as confirmation.
    detectedBankLabel() {
      return this.inputMode === "url" && this.form.bank ? this.bankLabel(this.form.bank) : "";
    },
  },
  watch: {
    "form.bank"() {
      // A stale account from a different bank must not carry over.
      this.form.recipientAccountId = null;
      this.errors.recipientAccountId = "";
      this.errors.bank = "";
    },
  },
  methods: {
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    accountLabel(account) {
      const name = account.name || account.accountName || "Account";
      return `${name} · ${account.accountNumber}`;
    },
    amount(record) {
      const value = Number(record.amount);
      if (isNaN(value)) return "—";
      return this.$filters.formatPrice(value, record.currency || null);
    },
    onRefInput() {
      if (this.errors.transactionId) this.errors.transactionId = "";
    },
    setInputMode(mode) {
      if (this.inputMode === mode) return;
      this.inputMode = mode;
      this.errors.transactionId = "";
      this.errors.receiptUrl = "";
      this.errors.recipientAccountId = "";
      this.errors.bank = "";
      // In url mode the bank is derived from the pasted link, not chosen — keep them in sync.
      if (mode === "url") {
        this.form.recipientAccountId = null;
        this.form.bank = this.detectBankFromUrl(this.form.receiptUrl);
      }
    },
    // Map a receipt link's host to the bank that serves it.
    detectBankFromUrl(value) {
      if (!value) return null;
      let host = "";
      try {
        host = new URL(value.trim()).hostname.toLowerCase();
      } catch {
        return null;
      }
      if (host === "cbe.com.et" || host.endsWith(".cbe.com.et")) return BANKS.cbe;
      if (host === "ethiotelecom.et" || host.endsWith(".ethiotelecom.et")) return BANKS.telebirr;
      if (host === "bankofabyssinia.com" || host.endsWith(".bankofabyssinia.com")) return BANKS.boa;
      return null;
    },
    isValidHttpUrl(value) {
      try {
        const url = new URL(value.trim());
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    },
    onUrlInput() {
      if (this.errors.receiptUrl) this.errors.receiptUrl = "";
      // The bank is identified from the link itself (null until a known host is recognised).
      this.form.bank = this.detectBankFromUrl(this.form.receiptUrl);
    },
    validate() {
      this.errors = { bank: "", recipientAccountId: "", transactionId: "", receiptUrl: "" };
      let ok = true;

      // Receipt-link mode: the only input is the url; the bank is identified from it.
      if (this.inputMode === "url") {
        const url = (this.form.receiptUrl || "").trim();
        if (!url) {
          this.errors.receiptUrl = "Paste the bank's receipt link";
          ok = false;
        } else if (!this.isValidHttpUrl(url)) {
          this.errors.receiptUrl = "Enter a valid link (it should start with https://)";
          ok = false;
        } else {
          const detected = this.detectBankFromUrl(url);
          if (!detected) {
            this.errors.receiptUrl =
              "We couldn't recognise this as a CBE, telebirr or Bank of Abyssinia receipt link.";
            ok = false;
          } else {
            this.form.bank = detected;
          }
        }
        return ok;
      }

      // Reference-id mode: bank is chosen explicitly.
      if (!this.form.bank) {
        this.errors.bank = "Select a bank";
        ok = false;
      }

      const ref = (this.form.transactionId || "").trim();
      if (!ref) {
        this.errors.transactionId = "Reference ID is required";
        ok = false;
      } else if (!/^[A-Za-z0-9]+$/.test(ref)) {
        this.errors.transactionId = "Reference ID may only contain letters and numbers";
        ok = false;
      }

      if (this.accountRequired && !this.form.recipientAccountId) {
        this.errors.recipientAccountId = `Select the bank account used for this ${this.bankLabel(this.form.bank)} transaction`;
        ok = false;
      }

      return ok;
    },
    submit() {
      if (!this.validate()) return;

      this.submitting = true;
      this.failure = null;
      const payload = { bank: this.form.bank };
      if (this.inputMode === "url") {
        payload.receiptUrl = this.form.receiptUrl.trim();
      } else {
        payload.transactionId = this.form.transactionId.trim();
        if (this.form.recipientAccountId) {
          payload.recipientAccountId = this.form.recipientAccountId;
        }
      }

      this.$store
        .dispatch(VERIFY_TRANSACTION, payload)
        .then((res) => {
          this.submitting = false;
          if (res) {
            this.result = res;
            this.$emit("verified");
          } else {
            // Surface the specific reason in the modal itself so it persists after
            // the (transient) toast disappears.
            this.failure =
              this.$store.getters.errorMsg ||
              "We couldn't verify this transaction. Check the details and try again.";
          }
        })
        .catch(() => {
          this.submitting = false;
          this.failure =
            this.$store.getters.errorMsg ||
            "We couldn't reach the verification service. Please check your connection and try again.";
        });
    },
    tryAgain() {
      // Return to the form with the entered values intact so they can be corrected.
      this.failure = null;
    },
    verifyAnother() {
      this.result = null;
      this.failure = null;
      this.form.transactionId = "";
      this.form.receiptUrl = "";
      this.errors = { bank: "", recipientAccountId: "", transactionId: "", receiptUrl: "" };
    },
    done() {
      this.closeModal();
    },
    closeModal() {
      this.$emit("close-verify-modal");
    },
    goToBankAccounts() {
      this.closeModal();
      this.$router.push({ name: BANK_ACCOUNTS_ROUTE });
    },
  },
  mounted() {
    this.$store.dispatch(GET_BANK_ACCOUNTS, { isActive: true, limit: 100 });
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  --dashboard-blue: #73c41d;
  --dashboard-blue-dark: #538c13;
  --dashboard-blue-soft: #eef6df;
  --dashboard-blue-border: #cfe6a3;
  --dashboard-slate: #34404a;
  --dashboard-muted: #76828b;
  --dashboard-border: #e3e8e6;
  --dashboard-canvas: #f4f6f3;
  --dashboard-green: #4aa12a;
  --dashboard-red: #e0464d;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--dashboard-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--dashboard-slate);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--dashboard-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dashboard-green);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--dashboard-slate);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--dashboard-canvas);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* Reference-id / receipt-link switch */
.mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 4px;
  margin-bottom: 16px;
  background: var(--dashboard-canvas);
  border: 1px solid var(--dashboard-border);
  border-radius: 12px;
}

.mode-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: var(--dashboard-muted);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.mode-btn .pi {
  font-size: 0.82rem;
}

.mode-btn:hover {
  color: var(--dashboard-slate);
}

.mode-btn.active {
  background: #ffffff;
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06) !important;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field > span {
  color: var(--dashboard-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field > span small {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.field input,
.field select {
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.field select:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.field select:disabled {
  background: var(--dashboard-canvas);
  color: var(--dashboard-muted);
  cursor: not-allowed;
}

.form-text.text-danger {
  margin-top: 4px;
  color: var(--dashboard-red);
  font-size: 0.74rem;
  font-weight: 500;
  position: relative;
  left: 4px;
}

.hint {
  margin-top: 4px;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.hint.hint-detected {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--dashboard-blue-dark);
  font-weight: 600;
}

.hint.hint-detected .pi {
  font-size: 0.82rem;
}

.hint .link {
  color: var(--dashboard-blue-dark);
  font-weight: 600;
  cursor: pointer;
}

.hint .link:hover {
  text-decoration: underline;
}

/* Result */
.result-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 26px 22px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  border-radius: 50%;
  font-size: 1.4rem;
}

.result-section.is-success .result-icon {
  background: var(--ehs-color-success-soft);
  color: var(--dashboard-green);
}

.result-section.is-info .result-icon {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
}

.result-section.is-error .result-icon {
  background: var(--ehs-color-danger-soft);
  color: var(--dashboard-red);
}

.result-section.is-error > p {
  color: var(--dashboard-slate);
}

.result-section h3 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--dashboard-slate);
}

.result-section > p {
  margin: 6px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.84rem;
}

.result-grid {
  width: 100%;
  margin: 18px 0 0;
  display: grid;
  gap: 0;
  text-align: left;
  border-top: 1px solid var(--dashboard-border);
}

.result-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 2px;
  border-bottom: 1px solid var(--dashboard-border);
}

.result-row dt {
  margin: 0;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.result-row dd {
  margin: 0;
  color: var(--dashboard-slate);
  font-size: 0.88rem;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

.result-row dd.mono {
  font-family: var(--ehs-font-mono);
  font-size: 0.82rem;
}

.result-row dd.result-amount {
  font-family: var(--ehs-font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dashboard-blue-dark);
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--dashboard-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--dashboard-slate);
  border-color: var(--dashboard-border);
}

.ghost-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.primary-button {
  background: var(--dashboard-blue);
  color: #ffffff;
  border-color: var(--dashboard-blue);
}

.primary-button:hover:not(:disabled) {
  background: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-dark);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  animation: btn-spin 0.6s linear infinite;
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .modal-head {
    padding: 16px 16px 14px;
    gap: 10px;
  }

  .modal-head h2 {
    font-size: 1.2rem;
  }

  .modal-body {
    padding: 16px 14px 24px;
  }

  .form-section {
    padding: 14px 14px;
  }

  .modal-foot {
    padding: 12px 14px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-foot .ghost-button,
  .modal-foot .primary-button {
    width: 100%;
  }
}
</style>
